import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  BarChart2,
  Check,
  Database,
  LayersPlus,
  LayoutGrid,
  Map as MapIcon,
  MessageSquare,
  Search,
  X,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { isPlainLeftClick, workspaceChatPath } from '../../lib/navigation';
import { chatApi } from '../../api/chatApi';
import { workspaceApi } from '../../api/workspaceApi';

const MODAL_TABS = [
  { id: 'overview', label: 'Overview', icon: LayoutGrid, showCount: false },
  { id: 'chats', label: 'Chats', icon: MessageSquare, showCount: true },
  { id: 'plots', label: 'Plots', icon: BarChart2, showCount: true },
  { id: 'maps', label: 'Maps', icon: MapIcon, showCount: true },
];

const WorkspaceSummaryModal = ({
  workspace,
  isOpen,
  onClose,
  onOpenWorkspace,
  onUpdateWorkspace,
  onSelectArtefact,
  initialTab = 'overview',
  showOpenWorkspace = true,
  prefetchedHistory,
  historyLoading = false,
}) => {
  const usesPrefetchedHistory = prefetchedHistory !== undefined;
  const navigate = useNavigate();
  const [editingName, setEditingName] = useState(false);
  const [editingDescription, setEditingDescription] = useState(false);
  const [savingName, setSavingName] = useState(false);
  const [savingDescription, setSavingDescription] = useState(false);
  const [nameDraft, setNameDraft] = useState(workspace?.name || '');
  const [descriptionDraft, setDescriptionDraft] = useState(workspace?.description || '');
  const [activeTab, setActiveTab] = useState(initialTab);
  const [chats, setChats] = useState([]);
  const [artefacts, setArtefacts] = useState([]);
  const [isInternalHistoryLoading, setIsInternalHistoryLoading] = useState(false);
  const [historySearchQuery, setHistorySearchQuery] = useState('');
  const isHistoryLoading = usesPrefetchedHistory ? historyLoading : isInternalHistoryLoading;

  useEffect(() => {
    setEditingName(false);
    setEditingDescription(false);
    setSavingName(false);
    setSavingDescription(false);
    setNameDraft(workspace?.name || '');
    setDescriptionDraft(workspace?.description || '');
  }, [workspace]);

  useEffect(() => {
    if (isOpen) {
      setActiveTab(initialTab);
      setHistorySearchQuery('');
    }
  }, [isOpen, initialTab]);

  const loadHistory = useCallback(async () => {
    if (!workspace?.id) return;

    setIsInternalHistoryLoading(true);
    try {
      const [chatData, artefactData] = await Promise.all([
        chatApi.getChats(workspace.id),
        workspaceApi.getWorkspaceArtefacts(workspace.id),
      ]);
      setChats(chatData);
      setArtefacts(artefactData);
    } catch (err) {
      console.error('Failed to load workspace history:', err);
      setChats([]);
      setArtefacts([]);
    } finally {
      setIsInternalHistoryLoading(false);
    }
  }, [workspace?.id]);

  useEffect(() => {
    if (!usesPrefetchedHistory) return;
    setChats(prefetchedHistory.chats ?? []);
    setArtefacts(prefetchedHistory.artefacts ?? []);
  }, [usesPrefetchedHistory, prefetchedHistory]);

  useEffect(() => {
    if (!isOpen || !workspace?.id || usesPrefetchedHistory) return;
    loadHistory();
  }, [isOpen, workspace?.id, loadHistory, usesPrefetchedHistory]);

  const plotArtefacts = useMemo(
    () => artefacts.filter(art => art.type === 'chart'),
    [artefacts]
  );
  const mapArtefacts = useMemo(
    () => artefacts.filter(art => art.type === 'map'),
    [artefacts]
  );

  const tabCounts = useMemo(() => {
    const workspaceChatCount = Number(workspace?.chatsCount ?? workspace?.chats_count ?? 0);
    return {
      chats: isHistoryLoading ? workspaceChatCount : chats.length,
      plots: plotArtefacts.length,
      maps: mapArtefacts.length,
    };
  }, [isHistoryLoading, chats.length, plotArtefacts.length, mapArtefacts.length, workspace]);

  const filteredChats = useMemo(
    () =>
      chats.filter(chat =>
        (chat.title || 'Untitled Chat').toLowerCase().includes(historySearchQuery.toLowerCase())
      ),
    [chats, historySearchQuery]
  );

  const filteredPlots = useMemo(
    () =>
      plotArtefacts.filter(art =>
        art.title.toLowerCase().includes(historySearchQuery.toLowerCase()) ||
        (art.chat_title && art.chat_title.toLowerCase().includes(historySearchQuery.toLowerCase()))
      ),
    [plotArtefacts, historySearchQuery]
  );

  const filteredMaps = useMemo(
    () =>
      mapArtefacts.filter(art =>
        art.title.toLowerCase().includes(historySearchQuery.toLowerCase()) ||
        (art.chat_title && art.chat_title.toLowerCase().includes(historySearchQuery.toLowerCase()))
      ),
    [mapArtefacts, historySearchQuery]
  );

  const handleSaveName = useCallback(async () => {
    const trimmedName = nameDraft.trim();
    if (!trimmedName || savingName) return;

    setSavingName(true);
    try {
      await onUpdateWorkspace(workspace.id, { name: trimmedName });
      setEditingName(false);
    } catch (err) {
      console.error('Failed to update workspace name:', err);
    } finally {
      setSavingName(false);
    }
  }, [nameDraft, onUpdateWorkspace, savingName, workspace]);

  const handleSaveDescription = useCallback(async () => {
    if (savingDescription) return;

    setSavingDescription(true);
    try {
      await onUpdateWorkspace(workspace.id, { description: descriptionDraft.trim() });
      setEditingDescription(false);
    } catch (err) {
      console.error('Failed to update workspace description:', err);
    } finally {
      setSavingDescription(false);
    }
  }, [descriptionDraft, onUpdateWorkspace, savingDescription, workspace]);

  const cancelNameEdit = useCallback(() => {
    setNameDraft(workspace?.name || '');
    setEditingName(false);
  }, [workspace?.name]);

  const cancelDescriptionEdit = useCallback(() => {
    setDescriptionDraft(workspace?.description || '');
    setEditingDescription(false);
  }, [workspace?.description]);

  useEffect(() => {
    if (!isOpen || !workspace) return undefined;

    const onKeyDown = event => {
      if (event.key === 'Escape') {
        event.preventDefault();
        if (editingName) {
          cancelNameEdit();
          return;
        }
        if (editingDescription) {
          cancelDescriptionEdit();
          return;
        }
        onClose();
        return;
      }

      if (event.key !== 'Enter') return;

      const targetTag = event.target?.tagName?.toLowerCase();
      const isTextArea = targetTag === 'textarea';
      const isTypingField = targetTag === 'input' || targetTag === 'textarea';
      const isInteractiveElement = Boolean(
        event.target?.closest?.('button, a, [role="button"], [contenteditable="true"], select')
      );

      if (editingName && targetTag === 'input') {
        event.preventDefault();
        handleSaveName();
        return;
      }

      if (editingDescription && isTextArea && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        handleSaveDescription();
        return;
      }

      if (editingName || editingDescription) return;

      if (!isTypingField && !isInteractiveElement) {
        event.preventDefault();
        if (showOpenWorkspace) {
          onOpenWorkspace(workspace.id);
        } else {
          onClose();
        }
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [
    isOpen,
    workspace,
    editingName,
    editingDescription,
    handleSaveName,
    handleSaveDescription,
    cancelNameEdit,
    cancelDescriptionEdit,
    onClose,
    onOpenWorkspace,
    showOpenWorkspace,
  ]);

  const handleViewArtefact = useCallback(
    artefact => {
      if (onSelectArtefact) {
        onSelectArtefact(artefact);
        onClose();
        return;
      }

      navigate(`/workspace/${workspace.id}`, { state: { viewArtefact: artefact } });
      onClose();
    },
    [navigate, onClose, onSelectArtefact, workspace?.id]
  );

  if (!isOpen || !workspace) return null;

  const sourceItems = Array.isArray(workspace.sources) ? workspace.sources : [];
  const savedName = workspace.name?.trim() || 'Untitled workspace';

  const renderInlineFieldActions = ({ onSave, onCancel, isSaving, saveDisabled }) => (
    <div className="mt-2 flex items-center justify-end gap-2">
      <button
        type="button"
        onClick={onCancel}
        disabled={isSaving}
        className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
      >
        <X size={12} />
        Cancel
      </button>
      <button
        type="button"
        onClick={onSave}
        disabled={isSaving || saveDisabled}
        className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-2.5 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-emerald-700 disabled:opacity-50"
      >
        <Check size={12} />
        {isSaving ? 'Saving...' : 'Save'}
      </button>
    </div>
  );

  const toSourceLabel = source => {
    if (typeof source === 'string') return source;
    return source?.name || source?.table_name || source?.dataset_name || source?.id || 'Unnamed dataset';
  };

  const formatChatDate = value => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Unknown';
    return `${date.toLocaleDateString()} • ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
  };

  const renderEmpty = message => (
    <p className="rounded-2xl border border-dashed border-slate-300 px-4 py-8 text-center text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
      {message}
    </p>
  );

  const renderLoading = () => (
    <div className="flex flex-col items-center justify-center gap-3 rounded-2xl border border-dashed border-slate-300 px-4 py-10 dark:border-slate-700">
      <div className="h-7 w-7 animate-spin rounded-full border-4 border-emerald-500/20 border-t-emerald-500" />
      <span className="text-xs font-medium text-slate-500 dark:text-slate-400">Loading...</span>
    </div>
  );

  const artefactActionClassName =
    'inline-flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-400 transition-colors hover:border-emerald-400 hover:bg-emerald-50 hover:text-emerald-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-500 dark:hover:border-emerald-600 dark:hover:bg-emerald-950/40 dark:hover:text-emerald-300';

  const renderGoToChatAction = (chatId, label) => (
    <Link
      to={workspaceChatPath(workspace.id, chatId)}
      onClick={e => {
        if (!isPlainLeftClick(e)) return;
        onClose();
      }}
      className={artefactActionClassName}
      title="Go to chat"
      aria-label={`Go to chat: ${label}`}
    >
      <ArrowRight size={14} strokeWidth={2.5} />
    </Link>
  );

  const renderHistoryRow = (rowKey, { icon: Icon, title, subtitle, action, onRowClick }) => (
    <li
      key={rowKey}
      className={cn(
        'group flex items-center gap-3 rounded-xl border border-slate-200/80 bg-white px-3 py-2.5 transition-colors dark:border-slate-700 dark:bg-slate-800/40',
        onRowClick
          ? 'cursor-pointer hover:border-emerald-300 hover:bg-emerald-50/80 dark:hover:border-emerald-700/60 dark:hover:bg-emerald-950/30'
          : 'hover:border-emerald-200 hover:bg-slate-50 dark:hover:border-emerald-800/40 dark:hover:bg-slate-800/70'
      )}
      onClick={onRowClick ? (e) => {
        if (e.target.closest('a, button')) return;
        onRowClick(e);
      } : undefined}
      onKeyDown={onRowClick ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onRowClick(e);
        }
      } : undefined}
      role={onRowClick ? 'button' : undefined}
      tabIndex={onRowClick ? 0 : undefined}
    >
      <span className="rounded-lg bg-slate-100 p-2 text-slate-500 transition-colors group-hover:bg-emerald-100 group-hover:text-emerald-700 dark:bg-slate-800 dark:text-slate-400 dark:group-hover:bg-emerald-900/50 dark:group-hover:text-emerald-300">
        <Icon size={14} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-slate-900 transition-colors group-hover:text-emerald-900 dark:text-slate-100 dark:group-hover:text-emerald-100">
          {title}
        </span>
        {subtitle ? (
          <span className="mt-0.5 block truncate text-xs text-slate-500 transition-colors group-hover:text-emerald-700/80 dark:text-slate-400 dark:group-hover:text-emerald-300/80">
            {subtitle}
          </span>
        ) : null}
      </span>
      {action ? (
        <div className="flex shrink-0 items-center gap-1 opacity-70 transition-opacity group-hover:opacity-100">
          {action}
        </div>
      ) : null}
    </li>
  );

  const renderArtefactList = (items, itemType) => {
    const ItemIcon = itemType === 'plots' ? BarChart2 : MapIcon;
    const emptyLabel = itemType;

    if (items.length === 0) {
      return renderEmpty(
        historySearchQuery
          ? `No ${emptyLabel} match your search.`
          : `No saved ${emptyLabel} in this workspace yet.`
      );
    }

    return (
      <ul className="max-h-72 space-y-2 overflow-auto">
        {items.map(art =>
          renderHistoryRow(art.id, {
            icon: ItemIcon,
            title: art.title,
            subtitle: `${art.chat_title ? `${art.chat_title} • ` : ''}${art.timestamp || 'Saved artefact'}`,
            onRowClick: () => handleViewArtefact(art),
            action:
              itemType === 'plots' ? (
                <button
                  type="button"
                  onClick={() => handleViewArtefact(art)}
                  className={artefactActionClassName}
                  title="Go to plot"
                  aria-label={`Go to plot: ${art.title}`}
                >
                  <ArrowRight size={14} strokeWidth={2.5} />
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => handleViewArtefact(art)}
                    className={artefactActionClassName}
                    title="Add layer to map"
                    aria-label={`Add layer to map: ${art.title}`}
                  >
                    <LayersPlus size={14} strokeWidth={2.5} />
                  </button>
                  {art.chat_id ? renderGoToChatAction(art.chat_id, art.title) : null}
                </>
              ),
          })
        )}
      </ul>
    );
  };

  const renderOverview = () => (
    <div className="space-y-5">
      <section>
        <p className="mb-2 text-sm font-semibold text-slate-800 dark:text-slate-200">Description</p>
        {editingDescription ? (
          <>
            <textarea
              rows={3}
              autoFocus
              value={descriptionDraft}
              onChange={e => setDescriptionDraft(e.target.value)}
              className="w-full resize-none rounded-2xl border border-slate-200 px-4 py-3 text-sm text-slate-700 outline-none transition-colors focus:border-emerald-500 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-300"
              placeholder="No description provided."
              title="Press Cmd/Ctrl + Enter to save"
            />
            <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">
              Press Cmd/Ctrl + Enter to save
            </p>
            {renderInlineFieldActions({
              onSave: handleSaveDescription,
              onCancel: cancelDescriptionEdit,
              isSaving: savingDescription,
              saveDisabled: false,
            })}
          </>
        ) : (
          <button
            type="button"
            onClick={() => {
              setDescriptionDraft(workspace.description || '');
              setEditingDescription(true);
            }}
            className="w-full rounded-2xl border border-dashed border-slate-300 bg-slate-50/80 px-4 py-3 text-left text-sm text-slate-600 transition-colors hover:border-emerald-300 hover:bg-emerald-50/50 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-400 dark:hover:border-emerald-800/70 dark:hover:bg-emerald-950/20"
          >
            {workspace.description?.trim() || 'No description provided. Click to add one.'}
          </button>
        )}
      </section>

      <section>
        <p className="mb-2 text-sm font-semibold text-slate-800 dark:text-slate-200">
          Datasets involved ({sourceItems.length})
        </p>
        {sourceItems.length > 0 ? (
          <div className="max-h-48 overflow-auto rounded-2xl border border-slate-200/80 bg-white/80 p-2 dark:border-slate-700 dark:bg-slate-800/40">
            <ul className="space-y-1.5">
              {sourceItems.map((source, index) => (
                <li
                  key={`${toSourceLabel(source)}-${index}`}
                  className="flex items-center gap-2 rounded-xl bg-emerald-50/60 px-3 py-2 text-sm text-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-200"
                >
                  <Database size={14} className="shrink-0" />
                  <span className="truncate">{toSourceLabel(source)}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="rounded-2xl border border-dashed border-slate-300 px-4 py-3 text-sm text-slate-500 dark:border-slate-700 dark:text-slate-400">
            No datasets linked to this workspace yet.
          </p>
        )}
      </section>
    </div>
  );

  const renderTabContent = () => {
    if (activeTab === 'overview') {
      return renderOverview();
    }

    if (isHistoryLoading) return renderLoading();

    if (activeTab === 'chats') {
      if (filteredChats.length === 0) {
        return renderEmpty(
          historySearchQuery ? 'No chats match your search.' : 'No chats in this workspace yet.'
        );
      }

      return (
        <ul className="max-h-72 space-y-2 overflow-auto">
          {filteredChats.map(chat =>
            renderHistoryRow(chat.id, {
              icon: MessageSquare,
              title: chat.title || 'Untitled Chat',
              subtitle: `${formatChatDate(chat.created_at)} • ${chat.message_count || 0} messages`,
              onRowClick: () => {
                navigate(workspaceChatPath(workspace.id, chat.id));
                onClose();
              },
              action: renderGoToChatAction(chat.id, chat.title || 'Untitled Chat'),
            })
          )}
        </ul>
      );
    }

    if (activeTab === 'plots') {
      return renderArtefactList(filteredPlots, 'plots');
    }

    return renderArtefactList(filteredMaps, 'maps');
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={`Workspace history for ${workspace.name || 'workspace'}`}
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/45 backdrop-blur-[2px]" />
      <div
        className="relative flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-3xl border border-emerald-200/70 bg-white shadow-2xl dark:border-emerald-900/50 dark:bg-slate-900"
        onClick={e => e.stopPropagation()}
      >
        <div className="shrink-0 border-b border-slate-200/80 px-6 pb-0 pt-6 dark:border-slate-800">
          <div className="mb-5 flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="mb-1 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">Workspace history</p>
              {editingName ? (
                <>
                  <input
                    type="text"
                    autoFocus
                    value={nameDraft}
                    onChange={e => setNameDraft(e.target.value)}
                    className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-base font-medium text-slate-900 outline-none transition-colors focus:border-emerald-500 dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                    placeholder="Workspace name"
                  />
                  {renderInlineFieldActions({
                    onSave: handleSaveName,
                    onCancel: cancelNameEdit,
                    isSaving: savingName,
                    saveDisabled: !nameDraft.trim(),
                  })}
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    setNameDraft(workspace.name || '');
                    setEditingName(true);
                  }}
                  className="w-full rounded-xl border border-dashed border-slate-300 bg-slate-50/70 px-3 py-2.5 text-left text-base font-medium text-slate-900 transition-colors hover:border-emerald-300 hover:bg-emerald-50/50 dark:border-slate-700 dark:bg-slate-800/60 dark:text-white dark:hover:border-emerald-800/70 dark:hover:bg-emerald-950/20"
                >
                  {savedName}
                </button>
              )}
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                Click name or description to edit
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-slate-800 dark:hover:text-slate-200"
              aria-label="Close workspace history"
            >
              <X size={16} />
            </button>
          </div>

          <div className="flex items-center gap-1 overflow-x-auto">
            {MODAL_TABS.map(tab => {
              const TabIcon = tab.icon;
              const isActive = activeTab === tab.id;
              const count = tab.showCount ? tabCounts[tab.id] : null;

              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => {
                    setActiveTab(tab.id);
                    setHistorySearchQuery('');
                  }}
                  className={cn(
                    'inline-flex shrink-0 items-center gap-2 border-b-2 px-3 py-2.5 text-sm font-medium transition-colors',
                    isActive
                      ? 'border-emerald-600 text-emerald-700 dark:border-emerald-400 dark:text-emerald-300'
                      : 'border-transparent text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                  )}
                >
                  <TabIcon size={14} />
                  <span>{tab.label}</span>
                  {count !== null && (
                    <span
                      className={cn(
                        'rounded-full px-2 py-0.5 text-xs font-semibold',
                        isActive
                          ? 'bg-emerald-600 text-white dark:bg-emerald-500 dark:text-emerald-950'
                          : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300'
                      )}
                    >
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          {activeTab !== 'overview' && (
            <div className="relative mb-4 w-full max-w-sm">
              <Search
                size={14}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              />
              <input
                type="text"
                value={historySearchQuery}
                onChange={e => setHistorySearchQuery(e.target.value)}
                placeholder={`Search ${activeTab}...`}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 py-2 pl-9 pr-3 text-sm text-slate-700 outline-none transition-colors focus:border-emerald-500 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-200"
              />
            </div>
          )}

          {renderTabContent()}
        </div>

        <div className="flex shrink-0 items-center justify-end gap-2 border-t border-slate-200/80 px-6 py-4 dark:border-slate-800">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            Close
          </button>
          {showOpenWorkspace && (
            <button
              type="button"
              onClick={() => onOpenWorkspace(workspace.id)}
              className="inline-flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-emerald-700"
            >
              Open workspace
              <ArrowRight size={14} />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default WorkspaceSummaryModal;
